<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { SHELL_PERIOD_PRESETS, dayFromIso } from '../helpers/reportsPeriod';

// Seletor unico de periodo da casa dos relatorios (mockup aprovado "Periodo
// unico nos Relatorios"): Hoje, 7 dias, 30 dias, Este mes ou Personalizado, o
// intervalo por extenso e a nota de que vale para todas as abas. Quem guarda o
// periodo e a URL; este componente so mostra e pede a troca.

const props = defineProps({
  // Periodo resolvido (helpers/reportsPeriod.js#resolveShellPeriod).
  period: {
    type: Object,
    required: true,
  },
  // So aparece na aba cuja tela sabe comparar com o periodo anterior: caixa de
  // selecao que nao muda nada seria pior que nenhuma.
  showCompare: {
    type: Boolean,
    default: false,
  },
  compare: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['preset', 'custom', 'update:compare']);

const { t, locale } = useI18n();

const isCustom = computed(
  () =>
    !SHELL_PERIOD_PRESETS.some(preset => preset.range === props.period.range)
);

const formatDay = day =>
  new Intl.DateTimeFormat(toIntlLocale(locale?.value), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(day);

const rangeText = computed(() =>
  t('REPORT.PERIOD_BAR.RANGE', {
    start: formatDay(props.period.startDay),
    end: formatDay(props.period.endDay),
  })
);

const selectCustom = () => {
  emit('custom', {
    startDay: props.period.startDay,
    endDay: props.period.endDay,
  });
};

const onDayInput = (edge, event) => {
  const day = dayFromIso(event.target.value);
  // Campo apagado ou data incompleta nao muda o periodo.
  if (!day) return;
  emit('custom', {
    startDay: edge === 'start' ? day : props.period.startDay,
    endDay: edge === 'end' ? day : props.period.endDay,
  });
};
</script>

<template>
  <div
    class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-3.5 py-3"
    data-testid="reports-period-bar"
  >
    <span
      id="reports-period-label"
      class="text-sm font-semibold text-n-slate-12"
    >
      {{ t('REPORT.PERIOD_BAR.LABEL') }}
    </span>

    <div
      role="group"
      aria-labelledby="reports-period-label"
      class="flex flex-wrap overflow-hidden rounded-lg border border-n-strong bg-n-solid-1"
    >
      <button
        v-for="preset in SHELL_PERIOD_PRESETS"
        :key="preset.range"
        type="button"
        class="min-h-11 px-3 text-sm font-semibold border-e border-n-strong outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
        :class="
          period.range === preset.range
            ? 'bg-n-blue-2 text-n-blue-11'
            : 'text-n-slate-12 hover:bg-n-alpha-2'
        "
        :aria-pressed="period.range === preset.range ? 'true' : 'false'"
        :data-testid="`reports-period-${preset.range}`"
        @click="emit('preset', preset.range)"
      >
        {{ t(preset.labelKey) }}
      </button>
      <button
        type="button"
        class="min-h-11 px-3 text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
        :class="
          isCustom
            ? 'bg-n-blue-2 text-n-blue-11'
            : 'text-n-slate-12 hover:bg-n-alpha-2'
        "
        :aria-pressed="isCustom ? 'true' : 'false'"
        :data-testid="`reports-period-${DATE_RANGE_TYPES.CUSTOM_RANGE}`"
        @click="selectCustom"
      >
        {{ t('REPORT.PERIOD_BAR.PRESETS.CUSTOM') }}
      </button>
    </div>

    <div
      class="inline-flex min-h-11 flex-wrap items-center gap-2 rounded-lg border border-n-strong bg-n-solid-1 px-3 text-sm tabular-nums text-n-slate-12"
      data-testid="reports-period-range"
    >
      <span
        class="i-ph-calendar-blank size-4 shrink-0 text-n-slate-11"
        aria-hidden="true"
      />
      <template v-if="isCustom">
        <label for="reports-period-start" class="sr-only">
          {{ t('REPORT.PERIOD_BAR.START') }}
        </label>
        <input
          id="reports-period-start"
          type="date"
          :value="period.startIso"
          :max="period.endIso"
          class="!mb-0 !h-9 !w-auto !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-n-brand rounded"
          data-testid="reports-period-start"
          @change="onDayInput('start', $event)"
        />
        <span aria-hidden="true">{{ t('REPORT.PERIOD_BAR.RANGE_JOIN') }}</span>
        <label for="reports-period-end" class="sr-only">
          {{ t('REPORT.PERIOD_BAR.END') }}
        </label>
        <input
          id="reports-period-end"
          type="date"
          :value="period.endIso"
          :min="period.startIso"
          class="!mb-0 !h-9 !w-auto !border-0 !bg-transparent !p-0 text-sm text-n-slate-12 tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-n-brand rounded"
          data-testid="reports-period-end"
          @change="onDayInput('end', $event)"
        />
      </template>
      <span v-else>{{ rangeText }}</span>
    </div>

    <label
      v-if="showCompare"
      class="inline-flex min-h-11 items-center gap-2 text-sm text-n-slate-12"
    >
      <input
        type="checkbox"
        class="!mb-0 size-4"
        :checked="compare"
        data-testid="reports-period-compare"
        @change="emit('update:compare', $event.target.checked)"
      />
      {{ t('REPORT.PERIOD_BAR.COMPARE') }}
    </label>

    <p class="m-0 text-xs text-n-slate-11 lg:ms-auto">
      {{ t('REPORT.PERIOD_BAR.NOTE') }}
    </p>
  </div>
</template>
